// Environment variable utility for Bun browser builds.
// Bun does NOT automatically inject import.meta.env for browser targets.
// The build.ts script uses Bun.build({ define: { "import.meta.env.VITE_X": JSON.stringify(value) } })
// to inline each VITE_* variable at build time.
// At runtime in the browser, import.meta.env may be undefined — we guard against that here.

function safeGetImportMetaEnv(key: string): string | undefined {
  try {
    const env = (import.meta as any)?.env;
    if (env && typeof env === "object" && key in env) {
      return env[key] as string;
    }
  } catch {
    // import.meta.env not available
  }
  return undefined;
}

function safeGetProcessEnv(key: string): string | undefined {
  try {
    if (typeof process !== "undefined" && process?.env && key in process.env) {
      return process.env[key] as string;
    }
  } catch {
    // process.env not available
  }
  return undefined;
}

export function getEnv(key: string, fallback: string): string {
  // 1. Try import.meta.env (inlined by Bun build define)
  const metaVal = safeGetImportMetaEnv(key);
  if (metaVal !== undefined && metaVal !== "") return metaVal;

  // 2. Try process.env (available in SSR / Node / Bun server contexts)
  const procVal = safeGetProcessEnv(key);
  if (procVal !== undefined && procVal !== "") return procVal;

  // 3. For VITE_* keys, also try BUN_PUBLIC_* equivalent
  if (key.startsWith("VITE_")) {
    const bunKey = key.replace(/^VITE_/, "BUN_PUBLIC_");
    const bunMetaVal = safeGetImportMetaEnv(bunKey);
    if (bunMetaVal !== undefined && bunMetaVal !== "") return bunMetaVal;
    const bunProcVal = safeGetProcessEnv(bunKey);
    if (bunProcVal !== undefined && bunProcVal !== "") return bunProcVal;
  }

  return fallback;
}

// API Base URL - automatically switches between local dev and production
// Priority: 1) Build-time injected env var, 2) Runtime hostname detection, 3) Fallback
export const API_BASE_URL = (() => {
  // Try build-time injected env var first
  const envUrl = getEnv("VITE_API_BASE_URL", "");
  if (envUrl) {
    return envUrl;
  }

  // Runtime detection as fallback (browser only)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

    if (isLocalhost) {
      return "http://localhost:5000/api";
    } else {
      // Production/deployed — use relative path for Firebase Hosting rewrite
      return "/api";
    }
  }

  // SSR fallback
  return "http://localhost:5000/api";
})();
